import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import useFeature from '../../components/SystemSettings/hooks/featureFlags/useFeature';
import { FEATURE_FLAGS_NAMES, HTTP_OPTIONS } from '@silverfort-nodelibs/common/utils/consts';
import { SYSTEM_MODES } from '@silverfort-nodelibs/common/utils/system-mode-config';
import { httpGet, updateUserToState } from '../../components/utils';

type SaasRestrictionsResponse = {
    data: {
        saasSupportEnabled: boolean;
        companyName: string;
        eulaAgree: boolean;
    };
};

interface SassRestrictionsContextType {
    restrictedSaasSupportMode: boolean;
    companyName: string;
    eulaAgree: boolean;
    setAgreeEula: () => void;
}

const DEFAULT_VALUE: SassRestrictionsContextType = {
    restrictedSaasSupportMode: false,
    companyName: '',
    eulaAgree: true,
    setAgreeEula: () => {}
};

const SassRestrictionsContext = createContext<SassRestrictionsContextType>(DEFAULT_VALUE);

export const SassRestrictionsProvider = ({ children }: { children: React.ReactNode }) => {
    const systemMode = useFeature(FEATURE_FLAGS_NAMES.SystemMode);

    const [restrictedMode, setRestrictedMode] = useState(DEFAULT_VALUE.restrictedSaasSupportMode);
    const [companyName, setCompanyName] = useState(DEFAULT_VALUE.companyName);
    const [eulaAgree, setEulaAgree] = useState(DEFAULT_VALUE.eulaAgree);

    useEffect(() => {
        const calcSaasRestrictions = async () => {
            await updateUserToState(this);
            const saasData = (await httpGet('/Settings/saasRestrictions', {
                [HTTP_OPTIONS.HANDLE_ERRORS]: true
            })) as unknown as SaasRestrictionsResponse;

            if (saasData.data) {
                if (!saasData.data.saasSupportEnabled) {
                    setRestrictedMode(true);
                }
                setCompanyName(saasData.data.companyName);
                setEulaAgree(saasData.data.eulaAgree);
            }
        };

        if (systemMode !== SYSTEM_MODES.ON_PREM) {
            calcSaasRestrictions();
        }
    }, [systemMode]);

    const setAgreeEula = useCallback(() => setEulaAgree(true), []);

    const restrictedSaasSupportMode = systemMode === SYSTEM_MODES.SAAS_SUPPORT && restrictedMode;

    return (
        <SassRestrictionsContext.Provider value={{ restrictedSaasSupportMode, companyName, eulaAgree, setAgreeEula }}>
            {children}
        </SassRestrictionsContext.Provider>
    );
};

export const useSassRestrictions = () => {
    return useContext(SassRestrictionsContext);
};
